import { useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  useResetAccessConfig,
  useSetAccessConfig,
} from "@hooks/mutations/use-access-config";
import {
  useAccessConfig,
  useAccessConfigCatalogue,
} from "@hooks/queries/use-access-config";
import { useMe } from "@hooks/queries/use-me";

/** Per-user-type view model: which capabilities are enabled + is-default flag. */
type TypeState = { enabled: Set<string>; isDefault: boolean };

const AccessConfigurationPage = () => {
  const { data: me, isLoading: meLoading } = useMe();
  const isAdmin = Boolean(me?.user?.is_admin);

  const { data: catalogue, isLoading: catLoading } = useAccessConfigCatalogue({
    enabled: isAdmin,
  });
  const { data: rows = [], isLoading: rowsLoading } = useAccessConfig({
    enabled: isAdmin,
  });

  const setConfig = useSetAccessConfig();
  const resetConfig = useResetAccessConfig();
  const isSaving = setConfig.isPending || resetConfig.isPending;

  // type -> {enabled set, isDefault}, seeded from the server rows.
  const byType = useMemo(() => {
    const map = new Map<string, TypeState>();
    for (const row of rows) {
      map.set(row.user_type, {
        enabled: new Set(row.capabilities),
        isDefault: row.is_default,
      });
    }
    return map;
  }, [rows]);

  if (meLoading) return <div className="p-6">Loading…</div>;
  if (!isAdmin) {
    return (
      <div className="text-muted-foreground p-6">
        You don’t have access to this page.
      </div>
    );
  }

  const capabilities = catalogue?.capabilities ?? [];
  const userTypes = catalogue?.user_types ?? [];

  const toggle = (userType: string, capability: string, next: boolean) => {
    const current = byType.get(userType)?.enabled ?? new Set<string>();
    const updated = new Set(current);
    if (next) updated.add(capability);
    else updated.delete(capability);
    setConfig.mutate({ userType, capabilities: Array.from(updated) });
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <h1 className="text-xl font-semibold">Access Configuration</h1>
      <p className="text-muted-foreground text-sm">
        Turn parts of the system on or off per user type. Changes apply to every user
        of that type; admins always have full access. A type left on{" "}
        <strong>Default</strong> uses the built-in access level.
      </p>

      {catLoading || rowsLoading ? (
        <div>Loading…</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Page / feature</TableHead>
              {userTypes.map((t) => (
                <TableHead key={t.value} className="text-center">
                  <div className="flex flex-col items-center gap-1">
                    <span>{t.label}</span>
                    {byType.get(t.value)?.isDefault ? (
                      <Badge variant="secondary">Default</Badge>
                    ) : (
                      <Badge variant="success">Customised</Badge>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isSaving || byType.get(t.value)?.isDefault}
                      onClick={() => resetConfig.mutate({ userType: t.value })}
                    >
                      Reset to default
                    </Button>
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {capabilities.map((cap) => (
              <TableRow key={cap.value}>
                <TableCell className="font-medium">{cap.label}</TableCell>
                {userTypes.map((t) => {
                  const enabled =
                    byType.get(t.value)?.enabled.has(cap.value) ?? false;
                  return (
                    <TableCell key={t.value} className="text-center">
                      <Switch
                        checked={enabled}
                        disabled={isSaving}
                        aria-label={`${t.label}: ${cap.label}`}
                        onCheckedChange={(next) => toggle(t.value, cap.value, next)}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default AccessConfigurationPage;
